import { HttpClient } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { combineLatest, Observable } from "rxjs";
import { map, shareReplay, switchMap } from "rxjs/operators";
import { AccountSettingsResource, OrganizationSummaryResource } from "../models";
import { DocumentSourceResource } from "../models/document-source-resource.model";
import { DocumentSourceUserResource } from "../models/document-source-user-resource.model";
import { DocumentSource } from "../models/document-source.model";
import { OrganizationUserResource } from "../models/organization-user-resource.model";
import { RuleResource } from "../models/rule-resource.model";
import { RuleType } from "../models/rule-type.model";
import { UserDetailsResource } from "../models/user-details-resource.model";
import { User } from "../models/user.model";

@Injectable({ providedIn: "root" })
export class ProfileService {
    private httpClient = inject(HttpClient);

    private currentUser$ = this.httpClient.get<OrganizationUserResource>(`/api/users/current`).pipe(shareReplay(1));

    private profileData$ = this.httpClient.get<UserDetailsResource & { ipAddress?: string }>(`/api/users/profileData`).pipe(shareReplay(1));

    public getAllUsers$(includeNetwork = false): Observable<Array<DocumentSourceUserResource>> {
        return this.httpClient.get<Array<DocumentSourceUserResource>>(`/api/users`, {
            params: { includeNetwork: includeNetwork.toString() }
        });
    }

    public getCurrentUser$(): Observable<OrganizationUserResource> {
        return this.currentUser$;
    }

    public getProfileData$(): Observable<UserDetailsResource & { ipAddress?: string }> {
        return this.profileData$;
    }

    public updateProfile$(payload: { userId: string; profile: UserDetailsResource }, _options?: any): Observable<UserDetailsResource> {
        const { userId, profile } = payload;
        return this.httpClient.put<UserDetailsResource>(`/api/users/${encodeURIComponent(userId)}`, profile);
    }

    public getAssignedRules$(payload: { userId: string; ruleType?: RuleType }): Observable<Array<RuleResource>> {
        const { userId, ruleType } = payload;
        return this.httpClient.get<Array<RuleResource>>(`/api/users/${encodeURIComponent(userId)}/rules`, {
            params: ruleType ? { ruleType } : undefined
        });
    }

    public getDocumentSources$(userId?: string): Observable<Array<DocumentSource>> {
        if (userId) {
            return this.httpClient.get<Array<DocumentSource>>(`/api/users/${encodeURIComponent(userId)}/documentsources`);
        }

        return this.getUserId$().pipe(switchMap((id) => this.httpClient.get<Array<DocumentSource>>(`/api/users/${encodeURIComponent(id)}/documentsources`)));
    }

    public getReleaseTypeDocumentSources$(userId?: string): Observable<Array<DocumentSourceResource>> {
        if (userId) {
            return this.httpClient.get<Array<DocumentSourceResource>>(`/api/users/${encodeURIComponent(userId)}/releasetypedocumentsources`);
        }

        return this.getUserId$().pipe(
            switchMap((id) => this.httpClient.get<Array<DocumentSourceResource>>(`/api/users/${encodeURIComponent(id)}/releasetypedocumentsources`))
        );
    }

    public getMergedProfileData$(): Observable<UserDetailsResource & OrganizationUserResource & AccountSettingsResource> {
        return combineLatest([this.getCurrentUser$(), this.getProfileData$()]).pipe(map(([currentUser, profileData]) => ({ ...currentUser, ...profileData })));
    }

    public getUserId$(): Observable<string> {
        return this.getProfileData$().pipe(map((data: any) => data.userId as string));
    }

    public getRole$(): Observable<string> {
        return this.getCurrentUser$().pipe(map((user) => user?.role?.toString() || ""));
    }

    public getOrganizationId$(): Observable<string> {
        return this.getCurrentUser$().pipe(map((user) => user?.activeOrganization?.id?.toString() || ""));
    }

    public getActiveOrganization$(): Observable<OrganizationSummaryResource> {
        return this.getCurrentUser$().pipe(map((user) => user?.activeOrganization));
    }

    public getParentNetwork$(): Observable<{ networkId: string | null }> {
        return this.getMergedProfileData$().pipe(map((profile: any) => ({ networkId: profile?.administerOfNetworkObjectId || null })));
    }

    public isNetworkAdmin$(): Observable<boolean> {
        return this.getProfileData$().pipe(map((data: any) => !!(data as User).isNetworkAdmin));
    }
}
